import { updateProductSchema, type Product, type UpdateProductInput } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useProducts, useUpdateProduct } from '../../../api/consumers';
import { ProductConfigurationPanel } from './ProductConfigurationPanel';
import { ProductDialog } from './ProductDialog';
import { ProductList } from './ProductList';

/**
 * Impostazioni › Prodotti (PRD-003 US-2, PAGE-007): i prodotti con il loro prefisso (DEC-003) e,
 * per quello selezionato, la configurazione letta dai consumer: lunghezza minima della descrizione,
 * categorie nei due set per canale, regola paziente per committente. Riservata all'amministratore.
 */
export function ProductsPage() {
  const productList = useProducts();
  const updateProduct = useUpdateProduct();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Product | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const products = productList.data?.products ?? [];
  const selected = products.find((product) => product.id === selectedId) ?? products[0] ?? null;

  const closeEdit = () => {
    setEditing(null);
    updateProduct.reset();
  };

  const submitProduct = (input: UpdateProductInput) => {
    if (!editing) return;
    updateProduct.mutate(
      { id: editing.id, changes: updateProductSchema.parse(input) },
      {
        onSuccess: (product) => {
          closeEdit();
          setNotice(`Prodotto ${product.name} (${product.prefix}) aggiornato.`);
        },
      }
    );
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h2">
          Prodotti
        </Typography>
        <Typography color="text.secondary">
          Ogni prodotto ha il prefisso del suo codice ticket e la configurazione che i suoi consumer leggono: categorie
          per canale, lunghezza minima della descrizione e regola paziente per committente. Le modifiche valgono per le
          segnalazioni successive.
        </Typography>
      </Stack>

      {productList.isPending && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Caricamento dei prodotti…</Typography>
        </Stack>
      )}

      {productList.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void productList.refetch()}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Prodotti non disponibili</AlertTitle>
          {productList.error.message}
        </Alert>
      )}

      {productList.data && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">
          <ProductList
            products={products}
            selectedId={selected?.id ?? null}
            onSelect={(product) => setSelectedId(product.id)}
            onEdit={setEditing}
          />
          {selected && <ProductConfigurationPanel key={selected.id} product={selected} onNotice={setNotice} />}
        </Stack>
      )}

      {editing && (
        <ProductDialog
          open
          key={editing.id}
          product={editing}
          error={updateProduct.error?.message}
          busy={updateProduct.isPending}
          onSubmit={submitProduct}
          onClose={closeEdit}
        />
      )}

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} message={notice} />
    </Stack>
  );
}
